import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  DateTime,
  ExportDropdown,
  HelpTooltip,
  Hours,
  Inline,
  Percent,
  PeriodFilter,
  Widget,
  YesNoFilter,
} from '~/components';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import TimeFiltersBar from '~/components/filters/TimeFiltersBar';
import TimeFiltersGroup from '~/components/filters/TimeFiltersGroup';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAuth,
  useDocumentTitle,
  usePercentFormat,
  useSearchParams,
  useSearchParamsConfig,
  useTimeFilters,
} from '~/hooks';
import useMemberFilters from '~/hooks/useMemberFilters';
import employmentTypes from '~/lookups/employment-types';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, intervalOptions, QuerySort } from '~/utils';
import intervalsByScope from '~/utils/intervalsByScope';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';

const columns = {
  capacity: { default: 'capacity', time_off_adjusted: 'capacityTimeOffAdjusted' },
  billableUtilization: { default: 'billableUtilization', time_off_adjusted: 'billableUtilizationTimeOffAdjusted' },
  clientUtilization: { default: 'clientUtilization', time_off_adjusted: 'clientUtilizationTimeOffAdjusted' },
  productiveUtilization: {
    default: 'productiveUtilization',
    time_off_adjusted: 'productiveUtilizationTimeOffAdjusted',
  },
  totalUtilization: { default: 'totalUtilization', time_off_adjusted: 'totalUtilizationTimeOffAdjusted' },
};

function UtilizationByTimeUnit({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const searchParamsConfig = useSearchParamsConfig();

  const memberFilters = useMemberFilters(() => ({
    memberEmploymentTypes: { default: [employmentTypes.employee], ...searchParamsConfig.memberEmploymentTypes },
    memberBillableTypeId: { default: 'billable', ...searchParamsConfig.memberBillableTypeId },
  }));
  const timeFilters = useTimeFilters({ keys: ['timeApprovalStatuses', 'timeClientApprovalStatuses'] });

  const [params, setParams] = useState({
    period: null,
    unit: '',
    utilizationMode: 'default',
    includeMembersWithoutCapacity: false,
    sort: new QuerySort('start', 'asc'),
    ...memberFilters.filters,
    ...timeFilters.filters,
  });

  // Init and sync URL search params
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervalOptions.this_year,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            week: intervalOptions.past_12_weeks,
            month: intervalOptions.this_year,
          },
        },
        unit: { default: 'month', valid: ['week', 'month'] },
        utilizationMode: {
          valid: ['default', 'time_off_adjusted'],
          default: workspace.defaultAdjustForTimeOff ? 'time_off_adjusted' : 'default',
        },
        includeMembersWithoutCapacity: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        sort: { default: new QuerySort('start', 'asc'), ...searchParamsConfig.sort },
        ...memberFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [searchParamsConfig, memberFilters, workspace.defaultAdjustForTimeOff, timeFilters],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      includeMembersWithoutCapacity: params.includeMembersWithoutCapacity ? 'true' : undefined,
      sort: params.sort,
      ...memberFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleUtilizationModeChange = (utilizationMode) => {
    let sort = params.sort;

    const key = _.findKey(columns, (v) => v[params.utilizationMode] === params.sort.column);

    if (key) {
      const column = columns[key][utilizationMode];
      sort = new QuerySort(column, params.sort.direction);
      setQuery((state) => ({ ...state, status: 'sorting' }));
    }

    setParams({ ...params, sort, utilizationMode });
    searchParams.set({ utilizationMode, sort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          placeholder="Date Range"
          value={params.period}
          intervals={intervalsByScope[params.unit]}
          onChange={({ value }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                week: intervalOptions.past_12_weeks,
                month: intervalOptions.this_year,
              }[value],
            })
          }
        />

        <YesNoFilter
          icon="filter"
          placeholder="Members without capacity"
          value={params.includeMembersWithoutCapacity ? 'yes' : 'no'}
          onChange={({ target: { value } }) => handleApplyFilters({ includeMembersWithoutCapacity: value === 'yes' })}
        />

        <Inline>
          <YesNoFilter
            data-testid="adjust_time_off"
            icon="gear"
            placeholder="Adjust for Paid Time Off"
            value={params.utilizationMode === 'time_off_adjusted' ? 'yes' : 'no'}
            onChange={({ target: { value } }) =>
              handleUtilizationModeChange(value === 'yes' ? 'time_off_adjusted' : 'default')
            }
          />
          <HelpTooltip
            style={{ marginLeft: '.5rem' }}
            message="Reduces the capacity by the number of Time Off hours taken during the period."
          />
        </Inline>

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;
  const records = _.orderBy(report.records, 'start');
  const totals = {
    default: {
      capacity: report.totals.capacity,
      billableUtilization: report.totals.billableUtilization,
      clientUtilization: report.totals.clientUtilization,
      productiveUtilization: report.totals.productiveUtilization,
      totalUtilization: report.totals.totalUtilization,
    },

    time_off_adjusted: {
      capacity: report.totals.capacityTimeOffAdjusted,
      billableUtilization: report.totals.billableUtilizationTimeOffAdjusted,
      clientUtilization: report.totals.clientUtilizationTimeOffAdjusted,
      productiveUtilization: report.totals.productiveUtilizationTimeOffAdjusted,
      totalUtilization: report.totals.totalUtilizationTimeOffAdjusted,
    },
  }[params.utilizationMode];

  const percentFormat = {
    tooltip: usePercentFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    ticks: usePercentFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  };

  return (
    <>
      <Report.Summary>
        <Widget style={{ height: '20rem' }}>
          <Line
            data={{
              labels: records.map(
                (record) =>
                  ({
                    week: moment(record.start).format(dateFormats.compactDate),
                    month: moment(record.start).format(dateFormats.monthYear),
                  })[params.unit],
              ),
              datasets: [
                {
                  label: 'Billable Utilization',
                  fill: false,
                  backgroundColor: colors.success,
                  borderColor: colors.success,
                  data: records.map((record) =>
                    params.utilizationMode === 'default'
                      ? Math.round(record.billableUtilization * 10000) / 100
                      : Math.round(record.billableUtilizationTimeOffAdjusted * 10000) / 100,
                  ),
                  tension: 0.4,
                },
                {
                  label: 'Client Utilization',
                  fill: false,
                  backgroundColor: colors.warning,
                  borderColor: colors.warning,
                  data: records.map((record) =>
                    params.utilizationMode === 'default'
                      ? Math.round(record.clientUtilization * 10000) / 100
                      : Math.round(record.clientUtilizationTimeOffAdjusted * 10000) / 100,
                  ),
                  tension: 0.4,
                },
                {
                  label: 'Productive Utilization',
                  fill: false,
                  backgroundColor: colors.danger,
                  borderColor: colors.danger,
                  data: records.map((record) =>
                    params.utilizationMode === 'default'
                      ? Math.round(record.productiveUtilization * 10000) / 100
                      : Math.round(record.productiveUtilizationTimeOffAdjusted * 10000) / 100,
                  ),
                  tension: 0.4,
                },
                {
                  label: 'Total Utilization',
                  fill: false,
                  backgroundColor: colors.primary50,
                  borderColor: colors.primary50,
                  data: records.map((record) =>
                    params.utilizationMode === 'default'
                      ? Math.round(record.totalUtilization * 10000) / 100
                      : Math.round(record.totalUtilizationTimeOffAdjusted * 10000) / 100,
                  ),
                  tension: 0.4,
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,

              plugins: {
                legend: {
                  onClick: null,
                  labels: {
                    pointStyleWidth: 10,
                    boxHeight: 7,
                    usePointStyle: true,
                  },
                },

                tooltip: {
                  callbacks: {
                    label: (tooltip) => percentFormat.tooltip.format(tooltip.parsed.y / 100),
                  },
                },
              },

              scales: {
                x: {
                  grid: { color: colors.grey10, drawBorder: false },
                  ticks: {
                    color: colors.grey40,
                    font: {
                      size: 12,
                      weight: 'bold',
                    },
                  },
                },

                y: {
                  grid: { color: colors.grey10, drawBorder: false },
                  min: 0,
                  suggestedMax: 100,
                  beginAtZero: true,
                  ticks: {
                    stepSize: 20,
                    callback: (value) => percentFormat.ticks.format(value / 100),
                  },
                },
              },
            }}
          />
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column minWidth="16rem" name="start" sticky onSort={onSort} sort={params.sort}>
              {{ month: 'Month', week: 'Week' }[params.unit]}
            </Table.Column>
            <Table.Column
              name={columns.capacity[params.utilizationMode]}
              width="8.5rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Utilization Capacity
            </Table.Column>
            <Table.Column name="billableHours" width="8.5rem" align="right" onSort={onSort} sort={params.sort}>
              Billable Hours
            </Table.Column>
            <Table.Column name="totalClientHours" width="8.5rem" align="right" onSort={onSort} sort={params.sort}>
              Client
              <br />
              Hours
            </Table.Column>
            <Table.Column name="totalProductiveHours" width="8.5rem" align="right" onSort={onSort} sort={params.sort}>
              Productive Hours
            </Table.Column>
            <Table.Column name="totalProjectHours" width="8.5rem" align="right" onSort={onSort} sort={params.sort}>
              Total Hours
            </Table.Column>
            <Table.Column
              name={columns.billableUtilization[params.utilizationMode]}
              width="8.5rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Billable Utilization
            </Table.Column>
            <Table.Column
              name={columns.clientUtilization[params.utilizationMode]}
              width="8.5rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Client Utilization
            </Table.Column>
            <Table.Column
              name={columns.productiveUtilization[params.utilizationMode]}
              width="8.5rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Productive Utilization
            </Table.Column>
            <Table.Column
              name={columns.totalUtilization[params.utilizationMode]}
              width="8.5rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Total Utilization
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((period) => {
              const values = {
                default: {
                  capacity: period.capacity,
                  billableUtilization: period.billableUtilization,
                  clientUtilization: period.clientUtilization,
                  productiveUtilization: period.productiveUtilization,
                  totalUtilization: period.totalUtilization,
                },

                time_off_adjusted: {
                  capacity: period.capacityTimeOffAdjusted,
                  billableUtilization: period.billableUtilizationTimeOffAdjusted,
                  clientUtilization: period.clientUtilizationTimeOffAdjusted,
                  productiveUtilization: period.productiveUtilizationTimeOffAdjusted,
                  totalUtilization: period.totalUtilizationTimeOffAdjusted,
                },
              }[params.utilizationMode];

              return (
                <Table.Row key={period.start}>
                  <Table.Cell>
                    {
                      { week: <DateTime value={period.start} />, month: moment(period.start).format('MMMM, YYYY') }[
                        params.unit
                      ]
                    }
                  </Table.Cell>
                  <Table.Cell>
                    <Hours value={values.capacity} />
                  </Table.Cell>
                  {/* This does not click-through because the Time Detail report doesn't support
                        including a subset of members based on member-permissions only. */}
                  <Table.Cell>
                    <Hours value={period.billableHours} />
                  </Table.Cell>
                  <Table.Cell>
                    <Hours value={period.totalClientHours} />
                  </Table.Cell>
                  <Table.Cell>
                    <Hours value={period.totalProductiveHours} />
                  </Table.Cell>
                  <Table.Cell>
                    <Hours value={period.totalProjectHours} />
                  </Table.Cell>
                  <Table.Cell>
                    <Percent value={values.billableUtilization} />
                  </Table.Cell>
                  <Table.Cell>
                    <Percent value={values.clientUtilization} />
                  </Table.Cell>
                  <Table.Cell>
                    <Percent value={values.productiveUtilization} />
                  </Table.Cell>
                  <Table.Cell>
                    <Percent value={values.totalUtilization} />
                  </Table.Cell>
                </Table.Row>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>
                <Hours value={totals.capacity} />
              </Table.Cell>
              <Table.Cell>
                {/* This does not click-through because the Time Detail report doesn't support
                    including a subset of members based on member-permissions only. */}
                <Hours value={report.totals.billableHours} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.totalClientHours} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.totalProductiveHours} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.totalProjectHours} />
              </Table.Cell>
              <Table.Cell>
                <Percent value={totals.billableUtilization} />
              </Table.Cell>
              <Table.Cell>
                <Percent value={totals.clientUtilization} />
              </Table.Cell>
              <Table.Cell>
                <Percent value={totals.productiveUtilization} />
              </Table.Cell>
              <Table.Cell>
                <Percent value={totals.totalUtilization} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label={{ month: 'Month', week: 'Week' }[params.unit]}
            isLoading={query.status !== 'ready'}
            partial={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const auth = useAuth();
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        placeholder="Include members without capacity"
        value={filters.includeMembersWithoutCapacity ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeMembersWithoutCapacity: value === 'yes' })}
      />

      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              week: intervalOptions.past_12_weeks,
              month: intervalOptions.this_year,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      {auth.members.viewTimeAndExpenses && <MemberFiltersGroup filters={filters} onChange={handleFilter} />}

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
      ]}
      {...props}
    />
  );
}

export default UtilizationByTimeUnit;
